import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import daily from "../../assets/Images/Daily Reflection-image.png";
import reflectionsData from "../../Data/reflection.json";

const FORCED_YEAR = 2025;

function DailyReflection() {
  const realNow = new Date();
  const forcedToday = new Date(
    FORCED_YEAR,
    realNow.getMonth(),
    realNow.getDate()
  );
  const [currentDate, setCurrentDate] = useState(forcedToday);
  const [selectedDate, setSelectedDate] = useState(forcedToday);
  const [reflection, setReflection] = useState(null);

  const fetchReflection = (dateObj) => {
    const dateString = dateObj.toLocaleDateString("en-CA");
    const data = reflectionsData[dateString];
    setReflection(data || null);
  };

  useEffect(() => {
    fetchReflection(selectedDate);
  }, []);

  const handleDateClick = (date) => {
    setSelectedDate(date);
    fetchReflection(date);
  };

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const daysOfWeek = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

  const getDaysInMonth = (date) => {
    const month = date.getMonth();
    const firstDay = new Date(date.getFullYear(), month, 1);
    const lastDay = new Date(date.getFullYear(), month + 1, 0);
    const days = [];

    const startDay = (firstDay.getDay() + 6) % 7;
    for (let i = 0; i < startDay; i++) days.push(null);

    for (let day = 1; day <= lastDay.getDate(); day++) {
      days.push(new Date(date.getFullYear(), month, day));
    }
    return days;
  };

  const navigateMonth = (dir) => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + (dir === "next" ? 1 : -1));
      return newDate;
    });
  };

  const isToday = (date) => date.toDateString() === forcedToday.toDateString();
  const isSelected = (date) =>
    date.toDateString() === selectedDate.toDateString();

  const days = getDaysInMonth(currentDate);

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-2 py-12">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-start">
          <div className="lg:w-1/2 space-y-6">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-blue-900">
              தினமும் நம் சிந்தனைக்கு
            </h1>

            {reflection ? (
              <div className="space-y-3 sm:space-y-4 text-gray-700 leading-relaxed text-justify">
                <p className="text-base sm:text-lg font-semibold text-red-800">
                  {reflection.caption}
                </p>
                <p className="text-base sm:text-lg font-bold leading-snug tracking-tight">
                  {reflection.title}
                </p>

                <p className="text-sm sm:text-base italic">
                  {reflection.subTitle}
                </p>
                <p className="text-sm sm:text-base italic">
                  {reflection.subTitle1}
                </p>
                <p className="text-sm sm:text-base lg:text-lg font-medium">
                  {reflection.content}
                </p>
              </div>
            ) : (
              <p className="text-gray-500 text-sm sm:text-base">
                No reflection available for this date.
              </p>
            )}
          </div>

          <div className="lg:w-1/2 w-full flex flex-col items-center">
            <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 w-full max-w-md">
              <div className="text-center mb-4">
                <h3 className="text-[10px] sm:text-xs lg:text-sm font-bold text-gray-700 uppercase tracking-wide">
                  SOBRIETY IS A JOURNEY, <br /> NOT A DESTINATION.
                </h3>
              </div>

              <div className="bg-blue-900 rounded-xl p-3 sm:p-4 text-white w-full">
                <div className="flex items-center justify-between mb-2 sm:mb-4">
                  <button
                    onClick={() => navigateMonth("prev")}
                    className="p-1 hover:bg-blue-800 rounded"
                  >
                    <ChevronLeft size={18} />
                  </button>

                  {/* MONTH ONLY (YEAR REMOVED) */}
                  <h4 className="font-semibold text-xs sm:text-sm lg:text-base">
                    {monthNames[currentDate.getMonth()]}
                  </h4>

                  <button
                    onClick={() => navigateMonth("next")}
                    className="p-1 hover:bg-blue-800 rounded"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>

                <div className="grid grid-cols-7 gap-1 mb-1 sm:mb-2">
                  {daysOfWeek.map((day) => (
                    <div
                      key={day}
                      className="text-[8px] sm:text-[10px] lg:text-xs text-center py-1 text-blue-200 font-medium"
                    >
                      {day}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-1">
                  {days.map((date, idx) => (
                    <div key={idx} className="aspect-square">
                      {date ? (
                        <button
                          onClick={() => handleDateClick(date)}
                          className={`w-full h-full flex items-center justify-center text-[10px] sm:text-xs lg:text-sm rounded-lg transition-colors
                            ${
                              isSelected(date)
                                ? "bg-blue-500 text-white font-bold"
                                : isToday(date)
                                ? "bg-blue-600 text-white font-semibold"
                                : "hover:bg-blue-800 text-white"
                            }`}
                        >
                          {date.getDate()}
                        </button>
                      ) : (
                        <div></div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="w-full">
        <img
          src={daily}
          alt="reflection decoration"
          className="w-full h-auto sm:h-[500px] md:h-[600px] lg:h-[610px] object-cover"
        />
      </div>
    </div>
  );
}

export default DailyReflection;
